/**
 * Utility functions for working with web workers with Comlink fallback
 */

import * as Comlink from "comlink";
import * as Sentry from "@sentry/browser";

/**
 * Creates a proxy object that communicates with a web worker, with fallback for environments
 * where Comlink might not be available (like Mobile Safari)
 * 
 * @param {Worker} worker - The web worker instance
 * @returns {Object} - A proxy object that mimics the worker's exposed API
 */
export const createWorkerProxy = (worker) => {
  try {
    // Set a flag to indicate we're using Comlink
    if (typeof window !== 'undefined') {
      window.__usingComlinkFallback = false;
    }
    return Comlink.wrap(worker);
  } catch (error) {
    Sentry.captureException(error);
    console.warn("Comlink.wrap failed, using fallback mechanism", error);
    
    // Set a flag to indicate we're using the fallback
    if (typeof window !== 'undefined') {
      window.__usingComlinkFallback = true;
    }
    
    // Fallback implementation when Comlink isn't available
    let messageId = 0;
    const callbacks = {};

    // Set up message handler
    worker.onmessage = (event) => {
      const { result, error, id } = event.data;
      if (callbacks[id]) {
        if (error) {
          callbacks[id].reject(new Error(error.message));
        } else {
          callbacks[id].resolve(result);
        }
        delete callbacks[id];
      }
    };

    // Create a proxy object that mimics the worker's API
    return new Proxy({}, {
      get: (target, prop) => {
        // Handle property access
        if (prop === 'then' || prop === 'catch') {
          return undefined; // Not a promise
        }
        
        // Return a function that sends a message to the worker
        return async (...args) => {
          return new Promise((resolve, reject) => {
            const id = messageId++;
            callbacks[id] = { resolve, reject };
            
            try {
              worker.postMessage({
                method: prop,
                args,
                id
              });
            } catch (error) {
              delete callbacks[id];
              reject(error);
            }
          });
        };
      }
    });
  }
};
